import { createSignal, createMemo, onMount, For, Show } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import StateDisplay from '../components/StateDisplay';
import Text from '../components/Text';
import { Tenant } from '../models/Tenant';
import styles from './Tenants.module.css';

export default function TenantAdmin() {
    const api = useApi();
    const navigate = useNavigate();
    const [tenants, setTenants] = createSignal<Tenant[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [filter, setFilter] = createSignal('');

    onMount(loadTenants);

    async function loadTenants() {
        setLoading(true);
        try {
            const res = await api.tenantAdmin.getAllTenants();
            if (isOk(res.data)) setTenants((res.data.value.tenants ?? []) as Tenant[]);
        } finally {
            setLoading(false);
        }
    }

    const filtered = createMemo(() => {
        const term = filter().trim().toLowerCase();
        if (!term) return tenants();
        return tenants().filter(t =>
            t.name.toLowerCase().includes(term) ||
            t.slug.toLowerCase().includes(term) ||
            t.id.toLowerCase().includes(term)
        );
    });

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <div class={styles.titleSection}>
                    <div class={styles.cardAvatar}>
                        <i class="fa-solid fa-building"></i>
                    </div>
                    <div class={styles.titleText}>
                        <h1><Text val="ADMIN_TENANT_MANAGEMENT" /></h1>
                        <p class={styles.subtitle}><Text val="ADMIN_TENANT_SUBTITLE" /></p>
                    </div>
                </div>
                <div class={styles.headerActions}>
                    <A href="/admin/tenants/new" class="btn btn-primary">
                        <i class="fa-solid fa-plus"></i>
                        <Text val="TENANT_CREATE" />
                    </A>
                </div>
            </div>

            <input
                type="text"
                class={styles.input}
                placeholder="Filter by name, slug, or id..."
                value={filter()}
                onInput={e => setFilter(e.currentTarget.value)}
                style={{ 'margin-bottom': 'var(--spacing-lg)' }}
            />

            <Show when={loading()}>
                <StateDisplay loading />
            </Show>

            <Show when={!loading() && tenants().length === 0}>
                <StateDisplay
                    empty
                    icon="fa-solid fa-building-circle-xmark"
                    message="ADMIN_TENANT_EMPTY"
                    helpText="ADMIN_TENANT_EMPTY_HELP"
                    action={{ label: 'TENANT_CREATE', onClick: () => navigate('/admin/tenants/new') }}
                />
            </Show>

            <Show when={!loading() && tenants().length > 0}>
                <div class={styles.grid}>
                    <For each={filtered()}>
                        {(tenant) => (
                            <A href={`/admin/tenants/${tenant.id}`} class={styles.tenantCard}>
                                <div class={styles.cardHead}>
                                    <div class={styles.cardAvatar}>
                                        <i class="fa-solid fa-building"></i>
                                    </div>
                                    <div class={styles.cardInfo}>
                                        <div class={styles.cardName}>{tenant.name}</div>
                                        <div class={styles.cardSlug}>@{tenant.slug}</div>
                                    </div>
                                </div>
                                <div class={styles.cardBadges}>
                                    <span class={tenant.isActive ? styles.badgeActive : styles.badgeInactive}>
                                        <i class={tenant.isActive ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'}></i>
                                        <Text val={tenant.isActive ? 'ACTIVE' : 'INACTIVE'} />
                                    </span>
                                </div>
                                <div class={styles.cardFooter}>
                                    <span title={tenant.id}>{tenant.id.slice(0, 8)}...</span>
                                    <span><Text val="VIEW_DETAILS" /> <i class="fa-solid fa-arrow-right"></i></span>
                                </div>
                            </A>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
